import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import Modal from "../../../../components/Modal";
import { Typography, Grid, IconButton } from "@mui/material";
import * as Yup from "yup";
import { FormikProvider, useFormik, FieldArray } from "formik";
import FormTextField from "../../../../components/FormTextField";
import FormSelectInput from "../../../../components/FormSelectInput";
import { get } from "lodash";
import Icon from "../../../../components/Icon";
import Button from "../../../../components/Button";
import useGetAllFeatures from "../../../../hooks/useGetAllFeatures";

const MainContainer = styled.div`
  padding-bottom: 40px;
  min-height: 100px;
  width: 100%;
  .numberField {
    margin-top: 20px;
  }
  .responseContainer {
    margin-top: 12px;
    .listItem {
      display: flex;
      flex-direction: row;
      margin-left: 10px;
    }
  }
`;

const AddFeatureModal = ({
  onSubmit,
  onCancel,
  isEditMode,
  isLoading,
  addedFeatures,
  featureData,
}) => {
  const { fetchAllFeatureList, groupFeatureList } = useGetAllFeatures();

  useEffect(() => {
    fetchAllFeatureList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validationSchema = Yup.object().shape({
    features: Yup.array().of(
      Yup.object().shape({
        groupId: Yup.string().trim().required("Required"),
        featureId: Yup.number()
          .required("Required")
          .test("featureId", "Duplicate", function (val) {
            const { features } = this.options.context; // Accessing the entire features array from form context

            // Check for duplicates in the features array
            const currentFeatureId = val;
            const featureIds = features.map((feature) => feature.featureId);

            // If the current featureId appears more than once in the array, return false
            const isUnique =
              featureIds.filter((id) => id === currentFeatureId).length === 1;

            return isUnique;
          }),
        value: Yup.string().trim().required("Required"),
      })
    ),
  });

  // Get unique feature from copyFeature which is not already added
  const uniqueFeature = useMemo(() => {
    if (isEditMode) {
      return featureData;
    }
    const featureIds = addedFeatures.map((feature) => feature.featureId);
    const uFeatures = featureData?.filter(
      (val) => !featureIds.includes(val.featureId)
    );
    return uFeatures ?? [];
  }, [featureData, addedFeatures, isEditMode]);

  const defaultObj = {
    groupId: null,
    featureId: null,
    value: "",
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      features: uniqueFeature?.length > 0 ? uniqueFeature : [defaultObj],
    },
    validationSchema: validationSchema,
    validateOnBlur: false,
    validateOnMount: true,
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  const filteredFeatures = useMemo(() => {
    if (!isEditMode) {
      const addedFeatureIds = [...addedFeatures]?.map((val) => val.featureId);

      const returnData =
        groupFeatureList?.map((val) => {
          const filteredFeatures = val?.features?.filter(
            (i) => !addedFeatureIds.includes(i.featureId)
          );
          return {
            ...val,
            features: filteredFeatures,
          };
        }) ?? [];
      return returnData.filter((val) => val?.features?.length > 0);
    } else {
      return groupFeatureList;
    }
  }, [isEditMode, groupFeatureList, addedFeatures]);

  const featuresData = useMemo(() => {
    return filteredFeatures.map((item) => {
      return {
        value: item.groupId,
        label: item.groupName,
        features: item.features.map((item1) => ({
          value: item1.featureId,
          label: item1.featureName,
        })),
      };
    });
  }, [filteredFeatures]);

  return (
    <Modal
      onSubmit={() => formik.submitForm()}
      confirmTitle={"Submit"}
      cancelTitle={"Cancel"}
      onCancel={onCancel}
      isLoading={isLoading}
      width={"70%"}
      isDisabled={!formik.isValid}
    >
      <MainContainer>
        <Typography
          variant="h6"
          textAlign={"center"}
          fontWeight={700}
          width={"100%"}
          marginBottom={2}
        >
          {isEditMode ? "Edit Feature" : "Add Features"}
        </Typography>
        <FormikProvider value={formik}>
          <FieldArray
            name="features"
            render={(arrayHelpers) => {
              const features = get(formik, "values.features") ?? [];
              return (
                <div className="formRows">
                  {features.map((item, index) => {
                    return (
                      <Grid key={index} container spacing={2} marginBottom={2}>
                        <Grid item xs={3}>
                          <FormSelectInput
                            name={`features.${index}.groupId`}
                            options={featuresData}
                            label="Feature Group"
                            disabled={isEditMode}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <FormSelectInput
                            name={`features.${index}.featureId`}
                            options={
                              featuresData.find((i) => i.value === item.groupId)
                                ?.features
                            }
                            label={"Feature Name"}
                            disabled={isEditMode}
                          />
                        </Grid>
                        <Grid item xs={features.length > 1 ? 4 : 5}>
                          <FormTextField
                            name={`features.${index}.value`}
                            label="Value"
                            required
                            placeholder={"Enter Value"}
                          />
                        </Grid>
                        <Grid item xs={1}>
                          {features.length > 1 && (
                            <IconButton
                              aria-label="close"
                              size="medium"
                              onClick={() => {
                                arrayHelpers.remove(index);
                              }}
                            >
                              <Icon icon="circleClose" height={24} width={24} />
                            </IconButton>
                          )}
                        </Grid>
                      </Grid>
                    );
                  })}
                  {!isEditMode && (
                    <Button
                      label={"Add New+"}
                      height={"30px"}
                      onClick={() => arrayHelpers.push(defaultObj)}
                    />
                  )}
                </div>
              );
            }}
          />
        </FormikProvider>
      </MainContainer>
    </Modal>
  );
};

export default AddFeatureModal;
